import React, { useReducer, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AccordionItem, Button, Input, Tooltip } from '@mm/components';
import {
  CheckOutlined,
  DeleteOutlined,
  LoadingOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { isEqual, omit } from 'lodash';
import validator from 'validator';
import css from './EventsStep.module.css';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { useXRayConfigurationCRUDActions } from '@mm/state/actions/XRayConfiguration';
import {
  hasConfiguredEvents,
  hasConfiguredIntegrations,
  selectXRayConfig,
} from '@mm/state/selectors/xrayConfigSelectors';
import isURL = validator.isURL;
import { Spin, Switch } from 'antd';
import { facebookConversionApiEvents } from '../SetupForm';
import { isDefaultFacebookConversionApiEventId } from '@mm/state/helpers/capi-helpers';
import classNames from 'classnames';
import { ReactComponent as CustomizeIcon } from 'assets/customize.svg';
import { ShopifyIcon } from '@mm/icons';

export const EventsStep = ({
  configReady,
  onSave,
}: {
  configReady?: boolean;
  onSave: (mode: string) => void;
}) => {
  const chatbotId = useSelector(activeChatbotIdSelector);

  const defaultConfiguration = useSelector(selectXRayConfig);

  const xRayConfigurationCRUDActions = useXRayConfigurationCRUDActions();

  const [busy, setBusy] = useState(false);
  const [addNewEventVisible, setAddNewEventVisible] = useState(false);

  const [newEventName, setNewEventName] = useState('');
  const [newEventUrl, setNewEventUrl] = useState('');

  const [isShopifySignalActive, setIsShopifySignalActive] = useState(false);
  const [isManualSignalActive, setIsManualSignalActive] = useState(false);

  useEffect(() => {
    if (defaultConfiguration?.mode === 'off') {
      setIsShopifySignalActive(false);
      setIsManualSignalActive(false);
    } else {
      setIsShopifySignalActive(defaultConfiguration?.mode === 'integrations');
      setIsManualSignalActive(defaultConfiguration?.mode === 'manual');
    }
  }, [defaultConfiguration]);

  const handleShopifySignalToggle = (
    checked: boolean,
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    setIsShopifySignalActive(checked);
    if (isManualSignalActive && checked) {
      setIsManualSignalActive(false);
    }
  };

  const handleManualSignalToggle = (
    checked: boolean,
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    setIsManualSignalActive(checked);
    if (isShopifySignalActive && checked) {
      setIsShopifySignalActive(false);
    }
  };

  const [state, dispatch] = useReducer(
    (state, action) => {
      switch (action.type) {
        case 'change':
          return {
            ...state,
            [action.payload.key]: action.payload.value,
          };
        case 'remove':
          return omit(state, [action.payload.key]);
        default:
          return state;
      }
    },
    defaultConfiguration?.configurations || {},
    state => state,
  );

  const hasChanges =
    !isEqual(defaultConfiguration?.configurations, state) ||
    !isEqual(
      defaultConfiguration?.mode,
      isShopifySignalActive
        ? 'integrations'
        : isManualSignalActive
        ? 'manual'
        : 'off',
    );

  const handleTurnOffNewFieldForm = () => {
    setAddNewEventVisible(false);
    setNewEventUrl('');
    setNewEventName('');
  };

  const handleTurnOnNewFieldForm = () => {
    setAddNewEventVisible(true);
  };

  const handleNewFieldCheckMarkBtnClick = () => {
    dispatch({
      type: 'change',
      payload: { key: newEventName, value: newEventUrl },
    });

    handleTurnOffNewFieldForm();
  };

  const isValidByKey = key => isURL(String(state[key]));

  const isValidByValue = val => isURL(val);

  const handleUrlChange = (key, value) => {
    dispatch({ type: 'change', payload: { key, value } });
  };

  const handleRemoveField = key => {
    dispatch({ type: 'remove', payload: { key } });
  };

  const handleSave = async () => {
    setBusy(true);

    try {
      const mode = isShopifySignalActive
        ? 'integrations'
        : isManualSignalActive
        ? 'manual'
        : 'off'; // Set mode to 'off' if neither signal is active

      const res =
        await xRayConfigurationCRUDActions.updateXRayEventsConfiguration(
          chatbotId,
          {
            ...defaultConfiguration,
            configurations: state,
            mode,
          },
        );
      if (hasConfiguredEvents(res) || hasConfiguredIntegrations(res)) {
        onSave(mode);
      }
    } finally {
      setBusy(false);
    }
  };

  const customEvents = Object.keys(state)
    .filter(k => !isDefaultFacebookConversionApiEventId(k))
    .map(key => ({
      key,
      title: key,
      info: '',
      placeholder: '',
    }));

  const eventsSource = [...facebookConversionApiEvents, ...customEvents];

  const bothModesAreOff = !isShopifySignalActive && !isManualSignalActive;

  return (
    <div
      className={classNames(css.wrapper, {
        [css.disabledWrapper]: !configReady,
      })}
    >
      <div className={css.header}>
        <div className={css.stepContentHeaderText}>
          <span className={css.stepContentTitle}>Events</span>
          <span className={css.stepContentDescription}>
            Select what events you want to send to Facebook
          </span>
        </div>
        <Button
          css={{ container: css.submitBtn }}
          onClick={handleSave}
          disabled={
            busy ||
            !hasChanges ||
            addNewEventVisible ||
            bothModesAreOff ||
            (isManualSignalActive &&
              Object.keys(state)
                .filter(k => state[k])
                .some(key => !isValidByKey(key))) ||
            (isManualSignalActive &&
              !hasConfiguredEvents(defaultConfiguration) &&
              Object.values(state).every(v => !v))
          }
        >
          Save
          {busy && (
            <Spin
              indicator={
                <LoadingOutlined className={css.submitBtnSpinner} spin />
              }
            />
          )}
        </Button>
      </div>
      <div className={css.container}>
        <div className={css.shopifySectionContainer}>
          <Tooltip
            title="When you activate Shopify Signal, Manual Signal will be stopped
          automatically if it has been setup. Only one can work at time."
            placement="bottom"
          >
            <div className={css.switchContainer}>
              <Switch
                checked={isShopifySignalActive}
                onChange={handleShopifySignalToggle}
              />
            </div>
          </Tooltip>
          <div className={css.sectionTitleContainer}>
            <div className={css.shopifyIcon}>
              <ShopifyIcon />
            </div>
            <div className={css.sectionTitleInfo}>
              <div>
                <span className={css.sectionTitle}>Shopify Signal</span>
                <span className={css.sectionTitleGreen}>Recommended</span>
              </div>
            </div>
          </div>
        </div>
        <AccordionItem
          className={classNames('integrations-item', css.sectionContainer)}
          closeOnOutsideClick={false}
          openOnMount={true}
          showArrow={true}
          caretStyleIcon
          icon={
            <Tooltip
              title="When you activate Manual set events based on URL, Shopify Signal will be
          stopped automatically. Only one can work at time."
              placement="bottom"
            >
              <div className={css.switchContainer}>
                <Switch
                  checked={isManualSignalActive}
                  onChange={handleManualSignalToggle}
                />
              </div>
            </Tooltip>
          }
          titleComponent={
            <div className={css.sectionTitleContainer}>
              <div className={css.customizeIcon}>
                <CustomizeIcon />
              </div>
              <div className={css.sectionTitleInfo}>
                <div>
                  <span className={css.sectionTitle}>
                    Manual set events based on URL{' '}
                  </span>
                </div>
              </div>
            </div>
          }
        >
          <div className={css.card}>
            <table className={css.table}>
              <thead>
                <tr>
                  <th scope="col">Events</th>
                  <th scope="col">URL contains</th>
                </tr>
              </thead>

              <tbody>
                {eventsSource
                  .filter(event => event.key !== 'activeOnSite')
                  .map(event => (
                    <tr key={event.key}>
                      <th scope="row">
                        <div className={css.eventCell}>
                          {event.title}
                          {isDefaultFacebookConversionApiEventId(event.key) && (
                            <Tooltip title={event.info} placement="bottom">
                              <QuestionCircleOutlined
                                className={css.infoIcon}
                              />
                            </Tooltip>
                          )}
                        </div>
                      </th>
                      <td>
                        <div className={css.eventField}>
                          <Input
                            type="text"
                            onChange={e =>
                              handleUrlChange(event.key, e.target.value.trim())
                            }
                            disabled={busy}
                            invalid={
                              state[event.key] && !isValidByKey(event.key)
                            }
                            value={state[event.key]}
                            placeholder={event.placeholder}
                            css={{ container: css.input }}
                          />

                          {!isDefaultFacebookConversionApiEventId(
                            event.key,
                          ) && (
                            <div className={css.eventActions}>
                              <Button
                                onClick={() => handleRemoveField(event.key)}
                                color="secondary"
                                css={{ container: css.deleteEventBtn }}
                              >
                                <DeleteOutlined />
                              </Button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}

                {addNewEventVisible && (
                  <div>
                    <tr key="new-event">
                      <th scope="row">
                        <div className={css.eventCell}>
                          <Input
                            type="text"
                            onChange={e =>
                              setNewEventName(e.target.value.trim())
                            }
                            disabled={busy}
                            invalid={!newEventName}
                            value={newEventName}
                            placeholder="Event name"
                            css={{ container: css.input }}
                          />
                        </div>
                      </th>
                      <td>
                        <div className={css.eventField}>
                          <Input
                            type="text"
                            onChange={e =>
                              setNewEventUrl(e.target.value.trim())
                            }
                            disabled={busy}
                            invalid={
                              !!newEventUrl && !isValidByValue(newEventUrl)
                            }
                            value={newEventUrl}
                            placeholder="Add Url"
                            css={{ container: css.input }}
                          />

                          <div className={css.eventActions}>
                            <Button
                              disabled={
                                !newEventUrl ||
                                (!!newEventUrl && !isValidByValue(newEventUrl))
                              }
                              color="green"
                              css={{ container: css.saveEventBtn }}
                              onClick={handleNewFieldCheckMarkBtnClick}
                            >
                              <CheckOutlined />
                            </Button>

                            <Button
                              onClick={() => handleTurnOffNewFieldForm()}
                              color="secondary"
                              css={{ container: css.deleteEventBtn }}
                            >
                              <DeleteOutlined />
                            </Button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </div>
                )}
              </tbody>
            </table>

            <Button
              css={{ container: css.addNewEventBtn }}
              color="secondary-blue"
              onClick={() => handleTurnOnNewFieldForm()}
              disabled={addNewEventVisible}
              leftIcon={<PlusOutlined />}
            >
              Event
            </Button>
          </div>
        </AccordionItem>
      </div>
    </div>
  );
};
